import { useCallback, useEffect, useRef, useState } from "react";
import { parseISO } from "date-fns";
import { useAxiosInstance } from "./useAxiosInstance";
import {
  DriverLogDetailedView,
  HosLogsApiFactory,
  ViolationType,
  ViolationViewDto,
} from "../data/service-proxies";
import { useVersion } from "react-admin";
import { getHosTimers } from "../../components/hos/hosUtils";

export const useDriverLog = (
  driverId: number | undefined,
  logDate: Date | undefined
) => {
  const [driverLog, setDriverLog] = useState<DriverLogDetailedView>();
  const version = useVersion();
  const axiosInstance = useAxiosInstance();
  const [loading, setLoading] = useState<boolean>(false);
  const requestSequence = useRef(0);

  const loadDriverLog = useCallback(
    async (showLoading: boolean) => {
      if (!driverId || !logDate || !axiosInstance) {
        return;
      }

      const requestId = ++requestSequence.current;
      if (showLoading) {
        setLoading(true);
      }

      try {
        const response = await HosLogsApiFactory(
          undefined,
          axiosInstance.defaults.baseURL,
          axiosInstance
        ).apiAppDriverlogsGetdriverdailylogPost({
          driverId,
          logDate: logDate.toISOString(),
        });

        if (requestId !== requestSequence.current) {
          return;
        }

        const { data } = response;
        if (!data.hosDetails) {
          setDriverLog(data);
          return;
        }

        const now = new Date();
        const hosTimers = getHosTimers(
          {
            ...data.hosDetails,
            cycleWorkTime: data.hosDetails?.cycleWorkTime,
          },
          now
        );

        const dayStart = parseISO(data.startOfDay!);
        if (hosTimers.breakViolationFrom) {
          const from =
            hosTimers.breakViolationFrom < dayStart
              ? dayStart
              : hosTimers.breakViolationFrom;
          const violation: ViolationViewDto = {
            isHosViolation: true,
            violationType: ViolationType.EightHoursViolation,
            startDateTime: from.toISOString(),
            startTime: from.getTime(),
            endDateTime: now.toISOString(),
            endTime: now.getTime(),
          };

          violation.violationError = violation.violationType;
          data.violations = [...(data.violations ?? []), violation];
        }

        if (hosTimers.shiftDriveViolationFrom) {
          const from =
            hosTimers.shiftDriveViolationFrom < dayStart
              ? dayStart
              : hosTimers.shiftDriveViolationFrom;
          const violation: ViolationViewDto = {
            isHosViolation: true,
            violationType: ViolationType.ElevenHoursViolation,
            startDateTime: from.toISOString(),
            startTime: from.getTime(),
            endDateTime: now.toISOString(),
            endTime: now.getTime(),
          };

          violation.violationError = violation.violationType;
          data.violations = [...(data.violations ?? []), violation];
        }

        if (hosTimers.shiftWorkViolationFrom) {
          const from =
            hosTimers.shiftWorkViolationFrom < dayStart
              ? dayStart
              : hosTimers.shiftWorkViolationFrom;
          const violation: ViolationViewDto = {
            isHosViolation: true,
            violationType: ViolationType.FourteenHoursViolation,
            startDateTime: from.toISOString(),
            startTime: from.getTime(),
            endDateTime: now.toISOString(),
            endTime: now.getTime(),
          };

          violation.violationError = violation.violationType;
          data.violations = [...(data.violations ?? []), violation];
        }

        if (hosTimers.cycleWorkViolationFrom) {
          const from =
            hosTimers.cycleWorkViolationFrom < dayStart
              ? dayStart
              : hosTimers.cycleWorkViolationFrom;
          const violation: ViolationViewDto = {
            isHosViolation: true,
            violationType: ViolationType.ThirtyFourHoursResetViolation,
            startDateTime: from.toISOString(),
            startTime: from.getTime(),
            endDateTime: now.toISOString(),
            endTime: now.getTime(),
          };

          violation.violationError = violation.violationType;
          data.violations = [...(data.violations ?? []), violation];
        }

        setDriverLog(data);
      } catch (e) {
        if (requestId === requestSequence.current) {
          console.error(e);
        }
      } finally {
        if (requestId === requestSequence.current) {
          setLoading(false);
        }
      }
    },
    [axiosInstance, driverId, logDate]
  );

  const refetch = useCallback(async () => {
    await loadDriverLog(false);
  }, [loadDriverLog]);

  useEffect(() => {
    if (!axiosInstance) {
      return;
    }

    void loadDriverLog(true);
  }, [axiosInstance, loadDriverLog, version]);

  useEffect(
    () => () => {
      requestSequence.current += 1;
    },
    []
  );

  return { driverLog, loading, refetch };
};
